import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'notesplus_theme';
const DEFAULT_THEME = 'midnight';

/** Valid theme ids: midnight (dark) */
export const THEME_IDS = ['midnight'];

function getInitialTheme() {
  return DEFAULT_THEME;
}

export const ThemeProvider = ({ children }) => {
  // Initial state from localStorage so first paint after refresh keeps saved theme
  const [theme, setThemeState] = useState(getInitialTheme);
  const [initialized, setInitialized] = useState(false);

  // On load: sync with main process (userData file) and apply to document
  useEffect(() => {
    if (typeof window === 'undefined') {
      setInitialized(true);
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        if (!cancelled) {
          setThemeState(DEFAULT_THEME);
          localStorage.setItem(STORAGE_KEY, DEFAULT_THEME);
          if (window.api?.saveTheme) window.api.saveTheme(DEFAULT_THEME);
        }
      } catch (e) {
        // ignore
      } finally {
        if (!cancelled) setInitialized(true);
      }
    };

    init();
    return () => { cancelled = true; };
  }, []);

  // Apply theme: data-theme and class="dark" for Tailwind dark mode.
  // Runs on mount (theme default) and whenever theme changes so first paint has a valid theme.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    
    // Set data-theme attribute
    root.setAttribute('data-theme', theme);
    
    // Set/remove dark class for Tailwind dark mode
    if (theme === 'midnight') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, theme);
    
    // Sync to main process
    if (window.api?.saveTheme) {
      window.api.saveTheme(theme);
    }
  }, [theme]);

  const setTheme = (next) => {
    const id = typeof next === 'string' && THEME_IDS.includes(next) ? next : DEFAULT_THEME;
    setThemeState(id);
  };

  const value = { theme, setTheme, initialized };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
